import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCreateOpportunity, useContacts } from '@/hooks/useCrm';
import { extractError } from '@/api/client';
import { PIPELINE_STAGES, STAGE_LABELS, type PipelineStage } from '@/types';

interface Props {
  open:    boolean;
  onClose: () => void;
}

const empty = () => ({
  contactId:         '',
  title:             '',
  amountFcfa:        '',
  stage:             'prospect' as PipelineStage,
  probabilityPct:    10,
  expectedCloseDate: '',
  notes:             '',
});

export const OpportunityFormDialog = ({ open, onClose }: Props) => {
  const [form, setForm] = useState(empty());
  const [error, setError] = useState<string | null>(null);
  const create = useCreateOpportunity();
  const { data: contactsData } = useContacts({ contactType: 'client', limit: 200 });
  const contacts = contactsData?.data ?? [];

  useEffect(() => {
    if (open) { setForm(empty()); setError(null); }
  }, [open]);

  const set = <K extends keyof ReturnType<typeof empty>>(k: K, v: ReturnType<typeof empty>[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        contactId:         form.contactId,
        title:             form.title.trim(),
        amountCents:       Math.round(parseFloat(form.amountFcfa || '0') * 100),
        stage:             form.stage,
        probabilityPct:    form.probabilityPct,
        expectedCloseDate: form.expectedCloseDate || undefined,
        notes:             form.notes.trim() || undefined,
        currency:          'FCFA',
      });
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const canSubmit = form.contactId && form.title.trim() && parseFloat(form.amountFcfa || '0') > 0;

  return (
    <Dialog open={open} onClose={onClose} title="Nouvelle opportunité commerciale" subtitle="Création dans le pipeline CRM" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <Field label="Client *">
          {contacts.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Aucun client en DB. Créez-en un d'abord dans Contacts.
            </div>
          ) : (
            <select
              value={form.contactId}
              onChange={(e) => set('contactId', e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
              required
            >
              <option value="">— Sélectionner —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName ? `${c.companyName} (${c.fullName})` : c.fullName}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Titre de l'opportunité *">
          <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Fourniture béton — Cocody 2026" required />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Montant (FCFA) *">
            <Input type="number" min="0" step="1000" value={form.amountFcfa} onChange={(e) => set('amountFcfa', e.target.value)} required placeholder="4500000" />
          </Field>
          <Field label="Stage">
            <select
              value={form.stage}
              onChange={(e) => set('stage', e.target.value as PipelineStage)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              {PIPELINE_STAGES.map((s) => (
                <option key={s} value={s}>{STAGE_LABELS[s]}</option>
              ))}
            </select>
          </Field>
          <Field label="Probabilité (%)">
            <Input type="number" min="0" max="100" value={form.probabilityPct} onChange={(e) => set('probabilityPct', parseInt(e.target.value, 10) || 0)} />
          </Field>
        </div>

        <Field label="Date de clôture prévisionnelle">
          <Input type="date" value={form.expectedCloseDate} onChange={(e) => set('expectedCloseDate', e.target.value)} />
        </Field>

        <Field label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)]"
            placeholder="Contexte, contacts à relancer, objections du client…"
          />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={create.isPending} disabled={!canSubmit}>Créer l'opportunité</Button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
